import React, { useEffect, useState } from 'react';
import { Card, CardBody, Image, Chip, Spinner } from '@nextui-org/react';
import { Clock } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import newsService, { NewsArticle } from '../services/newsService';

const NewsSection: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await newsService.getLatestNews({ limit: 10 });
      if (response.success && response.data) {
        setNewsItems(newsService.getSafeNewsArray(response.data));
      } else {
        setNewsItems([]);
        setError('News konnten nicht geladen werden');
      }
    } catch (err) {
      console.error('Fehler beim Laden der News:', err);
      setError('News konnten nicht geladen werden');
      setNewsItems([]);
    } finally {
      setLoading(false);
    }
  };

  const articleHref = (item?: NewsArticle) =>
    item?.slug ? `/news/${item.slug}` : '#';

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" color="primary" />
        </div>
      </section>
    );
  }

  if (newsItems.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="section-heading text-3xl font-bold tracking-tight mb-2">Top News</h2>
          <p className="text-gray-400">Alles Wichtige auf einen Blick</p>
        </div>
        <div className="text-center text-gray-500 py-12">
          <p>{error || 'Aktuell sind keine News verfügbar.'}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h2 className="section-heading text-3xl font-bold tracking-tight mb-2">Top News</h2>
        <p className="text-gray-400">Alles Wichtige auf einen Blick</p>
      </div>

      {/* Main News Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Featured Article */}
        <RouterLink to={articleHref(newsItems[0])} className="block lg:h-[400px]">
          <Card className="col-span-1 lg:col-span-1 card-lift bg-zinc-900 border border-zinc-800 h-full">
            <CardBody className="p-0 h-full">
              <div className="relative h-full">
                <Image
                  removeWrapper
                  alt={newsItems[0]?.title || 'News'}
                  className="z-0 w-full h-[300px] lg:h-[400px] object-cover"
                  src={newsService.getImageUrl(newsItems[0]) || '/icon.png'}
                />
                {newsItems[0]?.category && (
                  <div className="absolute top-4 left-4 z-10 flex gap-2">
                    <Chip
                      className="bg-zinc-800/80 text-white"
                      variant="solid"
                      size="sm"
                    >
                      {newsItems[0].category}
                    </Chip>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-6">
                  <h3 className="text-2xl font-bold mb-2 text-white">
                    {newsService.formatTitle(newsItems[0])}
                  </h3>
                  <p className="text-gray-300 line-clamp-2 mb-3">
                    {newsService.formatDescription(newsItems[0])}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {newsService.formatArticleTime(newsItems[0])}
                    </span>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </RouterLink>

        {/* Secondary Articles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:h-[400px]">
          {newsItems.slice(1, 3).map((item) => (
            <RouterLink key={item._id || item.id} to={articleHref(item)} className="block lg:h-full lg:min-h-0">
              <Card
                className="card-lift bg-zinc-900 border border-zinc-800 h-full"
              >
                <CardBody className="p-0 h-full">
                  <div className="flex flex-col sm:flex-row h-full">
                    <Image
                      removeWrapper
                      alt={item.title}
                      className="w-full sm:w-40 h-32 sm:h-full object-cover"
                      src={newsService.getImageUrl(item) || '/icon.png'}
                    />
                    <div className="p-4 flex-1 flex flex-col min-w-0">
                      <h4 className="font-semibold mb-1 line-clamp-2">
                        {newsService.formatTitle(item)}
                      </h4>
                      <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                        {newsService.formatDescription(item)}
                      </p>
                      <div className="mt-auto flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {newsService.formatArticleTime(item)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </RouterLink>
          ))}
        </div>
      </div>

      {/* More Articles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {newsItems.slice(3, 7).map((item) => (
          <RouterLink key={item._id || item.id} to={articleHref(item)} className="block">
            <Card
              className="card-lift bg-zinc-900 border border-zinc-800 h-full"
            >
              <CardBody className="p-0">
                <Image
                  removeWrapper
                  alt={item.title}
                  className="w-full h-40 object-cover"
                  src={newsService.getImageUrl(item) || '/icon.png'}
                />
                <div className="p-4">
                  <h4 className="font-semibold mb-2 line-clamp-2 text-sm">
                    {newsService.formatTitle(item)}
                  </h4>
                  <div className="text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock size={10} />
                      {newsService.formatArticleTime(item)}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </RouterLink>
        ))}
      </div>
    </section>
  );
};

export default NewsSection;
